import React, { useEffect, useState } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    MenuItem,
    Dialog,
    DialogContent,
    Divider,
    CircularProgress,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import AppDialogTitle from '../../ui/appDialogTitle';
import { EmployeeService } from '../../../service/employeeService';
import { isLocalDev } from '../../../utils/environment';
import { toast } from 'react-toastify';
import { initialValues } from './commonEmployeeData';
import { editFormValidationSchema } from './employeeFormValidationSchema';
import CitySelect from '../../common/citySelect';
import ProvinceSelect from '../../common/provinceSelect';
import { PasswordField } from '../../common/passwordField';
import { Colors } from '../../../styles/theme';
import SignatureDialog from '../../common/signatureDialog';
import SignatureDisplay from '../../common/signatureDisplay';
import { AttachmentService } from '../../../service/attachmentService';
import useDialogModal from '../../../hooks/useDialogModal';
import ConfirmationDialog from '../../ui/confirmationDialog';
import validLookup from '../../../utils/validLookup';
import PhoneNumberField from '../../common/phoneNumberField';
import { fromE164Format } from '../../../utils/phoneUtils';
import { useBusinessPreferences } from 'hooks/usePreferences';
import { ValidRoleService } from '../../../service/validDataService';
import SafeDialogContent from 'components/ui/SafeDialogContent';

export default function EmployeeEditForm({ open, onClose, onUserEdit, employeeRow }) {
    const [employee, setEmployee] = useState()
    const [attachments, setAttachments] = useState([])
    const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
    const [capturedSignature, setCapturedSignature] = useState(null);
    const [signatureChanged, setSignatureChanged] = useState(false);
    const [roles, setRoles] = useState([]);
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    // Get business preferences from the context (pre-loaded, no delay)
    const { preferences: businessPreferences } = useBusinessPreferences();

    // Get phone format from preferences or fallback to default
    const phoneFormat = businessPreferences?.phoneNumberFormat || '(###) ###-####';

    useEffect(() => {

        // get employee data
        (async () => {
            const response = await EmployeeService.getById(employeeRow.id)
            const { code, data } = response

            if (code === 0) {
                setEmployee(data)
            }
        })()

        // get attachments
        if (employeeRow) {
            (async () => {
                const attachments = await AttachmentService.getAttachment('employee', employeeRow.id)
                const { code, data } = attachments
                if (code === 0) {
                    setAttachments(data)
                }
            })()
        }

    }, [employeeRow])

    // Load roles dynamically from ValidRoleService
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await ValidRoleService.getAll();
                if (!mounted) return;
                setRoles(Array.isArray(data) ? data : []);
            } catch (_) {
                setRoles([]);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // Handle signature capture from dialog
    const handleSignatureSave = (signatureData) => {
        // Store signature locally, don't save immediately
        const signature = signatureData[0];
        
        let signatureWithPreview;
        
        if (signature.file) {
            // Hand-drawn signature with buffer data
            const blob = new Blob([new Uint8Array(signature.file)], { type: 'image/png' });
            const previewUrl = URL.createObjectURL(blob);
            
            signatureWithPreview = {
                ...signature,
                previewUrl: previewUrl
            };
        } else {
            // Uploaded signature with downloadurl
            signatureWithPreview = {
                ...signature,
                previewUrl: signature.downloadurl
            };
        }
        
        setCapturedSignature(signatureWithPreview);
        setSignatureChanged(true);
    };

    // Handle signature deletion
    const handleSignatureDelete = (attachmentId) => {
        if (attachmentId === 'temp') {
            // Delete captured signature
            if (capturedSignature && capturedSignature.previewUrl) {
                URL.revokeObjectURL(capturedSignature.previewUrl);
            }
            setCapturedSignature(null);
            setSignatureChanged(true);
        } else {
            // Delete existing signature with confirmation
            showConfirmationDialog(async (action) => {
                if (action === validLookup.confirmationDialogAction.ok) {
                    try {
                        const deleteResult = await AttachmentService.deleteAttachment(attachmentId);
                        const { code, message } = deleteResult;
                        
                        if (code === 0) {
                            toast.success(message);
                            // Remove deleted attachment from state
                            setAttachments(prev => prev.filter(att => att.id !== attachmentId));
                            setSignatureChanged(true);
                        } else {
                            toast.error('Failed to delete signature');
                        }
                    } catch (error) {
                        console.error('Error deleting signature:', error);
                        toast.error('Failed to delete signature');
                    }
                }
                closeConfirmationDialog();
            }, {
                title: 'Delete Signature',
                message: 'Are you sure you want to delete this signature? This action cannot be undone.',
            });
        }
    };

    // Handle opening signature dialog
    const handleEditSignature = () => {
        setSignatureDialogOpen(true);
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        // Convert phone numbers to E.164 format for database storage
        const formData = { ...values };
        
        // phone formatting is done server side

        // Add captured signature to form values if exists
        if (capturedSignature) {
            formData.signature = capturedSignature;
        }

        try {
            const response = await EmployeeService.update(formData)
            const { code, message } = response
            setSubmitting(false)

            if (code === 0) {
                toast.success(message);
                // Clean up signature preview URL
                if (capturedSignature && capturedSignature.previewUrl) {
                    URL.revokeObjectURL(capturedSignature.previewUrl);
                }
                onClose();
                onUserEdit(formData);
            } else {
                toast.success(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.log(error)
            }
            setSubmitting(false)
            toast.error('Failed to update record');
        }
    };

    return (
        <Dialog open={open} maxWidth={'md'}>
            <AppDialogTitle title={"Edit Employee"} onClose={onClose} />
            <SafeDialogContent>
                {
                    employee ?
                        <Formik
                            initialValues={{
                                ...initialValues,
                                ...Object.fromEntries(
                                    Object.entries(employee).map(([key, value]) => {
                                        // Convert E.164 phone numbers to national format for display
                                        if ((key === 'phone' || key === 'emergencyContactPhone1' || key === 'emergencyContactPhone2') 
                                            && value && businessPreferences?.countryCode) {
                                            return [key, fromE164Format(value, businessPreferences.countryCode)];
                                        }
                                        return [key, value === null || value === undefined ? '' : value];
                                    })
                                )
                            }}
                            validationSchema={editFormValidationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ values, setFieldValue, isSubmitting, dirty }) => (
                                <Form>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values.status === "active"}
                                                onChange={(e) =>
                                                    setFieldValue("status", e.target.checked ? "active" : "inactive")
                                                } />
                                        }
                                        name='status'
                                        label={values.status === "active" ? <Typography sx={{color: Colors.success}}>Active</Typography> 
                                        : <Typography sx={{color: Colors.danger}}>Inactive</Typography>}
                                    />

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="firstName"
                                                label="First Name"
                                                required
                                                fullWidth
                                            />
                                            <ErrorMessage name="firstName">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} sm={4} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="middleName"
                                                label="Middle Name"
                                                fullWidth
                                            />
                                            <ErrorMessage name="lastName">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} sm={4} mt={1}>
                                            <Field
                                                as={TextField}
                                                name="lastName"
                                                label="Last Name"
                                                required
                                                fullWidth
                                            />
                                            <ErrorMessage name="lastName">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Divider><Typography fontWeight='bold' variant='caption'>Address</Typography></Divider>
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <Field
                                                as={TextField}
                                                name="addrNo"
                                                label="Number"
                                                fullWidth
                                            />
                                            <ErrorMessage name="addrNo">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} sm={8}>
                                            <Field
                                                as={TextField}
                                                name="addrStreet"
                                                label="Street"
                                                fullWidth
                                            />
                                            <ErrorMessage name="addrStreet">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <Field
                                                as={TextField}
                                                name="addrUnit"
                                                label="Unit"
                                                fullWidth
                                            />
                                            <ErrorMessage name="addrUnit">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} sm={5}>
                                            <Field
                                                name="addrCity"
                                                component={CitySelect}
                                                size="large"
                                                countryId={1} // Pass the countryId as a prop
                                                label="City"
                                            />
                                            <ErrorMessage name="addrCity">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} sm={5}>
                                            <Field
                                                name="addrProvince"
                                                component={ProvinceSelect}
                                                size="large"
                                                countryId={1} // Pass the countryId as a prop
                                                label="Province"
                                            />
                                            <ErrorMessage name="addrProvince">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <Field
                                                as={TextField}
                                                name="addrPostal"
                                                label="Postal code"
                                                fullWidth
                                            />
                                            <ErrorMessage name="addrPostal">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Divider><Typography fontWeight='bold' variant='caption'>Contact info</Typography></Divider>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Field
                                                name="phone"
                                                label="Phone Number"
                                                component={PhoneNumberField}
                                                mask={phoneFormat}
                                                countryCode={businessPreferences?.countryCode}
                                                required
                                                fullWidth
                                            />
                                            <ErrorMessage name="phone">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Field
                                                as={TextField}
                                                name="email"
                                                label="Email"
                                                autoComplete="username"
                                                required
                                                fullWidth
                                            />
                                            <ErrorMessage name="email">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Field
                                                name="password"
                                                label="Password"
                                                component={PasswordField}
                                                fieldName="password"
                                                autoComplete="new-password"
                                                values={values}
                                                touched
                                                onChange={(e) => setFieldValue('password', e.target.value)}
                                            //setFieldValue={setFieldValue} 
                                            />
                                            <ErrorMessage name="password">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Field
                                                name="confirmpassword"
                                                label="Confirm Password"
                                                component={PasswordField}
                                                fieldName="confirmpassword"
                                                autoComplete="new-password"
                                                values={values}
                                                touched
                                                onChange={(e) => setFieldValue('confirmpassword', e.target.value)}
                                            //setFieldValue={setFieldValue} 
                                            />
                                            <ErrorMessage name="confirmpassword">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Divider><Typography fontWeight='bold' variant='caption'>Emergency contact</Typography></Divider>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                as={TextField}
                                                name="emergencyContactName1"
                                                label="Name"
                                                fullWidth
                                            />
                                            <ErrorMessage name="emergencyContactName1">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                name="emergencyContactPhone1"
                                                label="Phone"
                                                component={PhoneNumberField}
                                                mask={phoneFormat}
                                                countryCode={businessPreferences?.countryCode}
                                                fullWidth
                                            />
                                            <ErrorMessage name="emergencyContactPhone1">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                as={TextField}
                                                name="emergencyContactName2"
                                                label="Name"
                                                fullWidth
                                            />
                                            <ErrorMessage name="emergencyContactName2">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                name="emergencyContactPhone2"
                                                label="Phone"
                                                component={PhoneNumberField}
                                                mask={phoneFormat}
                                                countryCode={businessPreferences?.countryCode}
                                                fullWidth
                                            />
                                            <ErrorMessage name="emergencyContactPhone2">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Field
                                                as={TextField}
                                                name="role"
                                                label="Role"
                                                select
                                                required
                                                fullWidth
                                            >
                                                {roles.map((r) => (
                                                    <MenuItem key={r.id} value={r.name.toLowerCase()}>
                                                        {r.name}
                                                    </MenuItem>
                                                ))}
                                            </Field>
                                            <ErrorMessage name="role">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                        </Grid>
                                        {values.role === 'practitioner' && (
                                            <Grid item xs={12}>
                                                <Divider><Typography fontWeight='bold' variant='caption'>Signature</Typography></Divider>
                                                <SignatureDisplay
                                                    attachments={capturedSignature ? [
                                                        {
                                                            id: 'temp',
                                                            path: capturedSignature.previewUrl,
                                                            fileName: capturedSignature.originalname,
                                                            contentType: 'image/png'
                                                        },
                                                        ...attachments
                                                    ] : attachments}
                                                    onEdit={handleEditSignature}
                                                    onDelete={handleSignatureDelete}
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                disabled={isSubmitting || (!dirty && !signatureChanged)}
                                            >
                                                Save
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                        : <Typography><CircularProgress /> Loading data...</Typography>
                }

            </SafeDialogContent>
            
            {/* Signature Capture Dialog */}
            <SignatureDialog
                open={signatureDialogOpen}
                onClose={() => setSignatureDialogOpen(false)}
                onSignatureSave={handleSignatureSave}
            />
            
            {/* Confirmation Dialog for Delete */}
            <ConfirmationDialogComp
                label={{
                    positive: "Delete",
                    negative: "Cancel",
                }}
            />
        </Dialog>
    );
};